import { Clock, Save, CheckCircle, Wifi, WifiOff } from 'lucide-react';
import { useState, useEffect } from 'react';

interface DraftIndicatorProps {
  isDirty: boolean;
  isAutoSaving: boolean;
  lastSaved: Date | null;
}

export const DraftIndicator = ({ isDirty, isAutoSaving, lastSaved }: DraftIndicatorProps) => {
  const [isOnline, setIsOnline] = useState(navigator.onLine);
  const [timeAgo, setTimeAgo] = useState<string>('');

  // Surveiller la connexion
  useEffect(() => {
    const handleOnline = () => setIsOnline(true);
    const handleOffline = () => setIsOnline(false);

    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);

    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  // Mettre à jour le "temps écoulé" depuis la dernière sauvegarde
  useEffect(() => {
    if (!lastSaved) return;

    const updateTimeAgo = () => {
      const now = new Date();
      const diffMs = now.getTime() - lastSaved.getTime();
      const diffSeconds = Math.floor(diffMs / 1000);
      const diffMinutes = Math.floor(diffSeconds / 60);

      if (diffSeconds < 10) {
        setTimeAgo('à l\'instant');
      } else if (diffSeconds < 60) {
        setTimeAgo(`il y a ${diffSeconds}s`);
      } else if (diffMinutes < 60) {
        setTimeAgo(`il y a ${diffMinutes}min`);
      } else {
        setTimeAgo(`à ${lastSaved.toLocaleTimeString()}`);
      }
    };

    updateTimeAgo();
    const interval = setInterval(updateTimeAgo, 1000);

    return () => clearInterval(interval);
  }, [lastSaved]);

  const getStatus = () => {
    if (!isOnline) {
      return {
        icon: <WifiOff className="h-3 w-3" />,
        text: 'Hors ligne - Sauvegarde en attente',
        color: 'text-red-500',
        bg: 'bg-red-50'
      };
    }

    if (isAutoSaving) {
      return {
        icon: <Save className="h-3 w-3 animate-pulse" />,
        text: 'Sauvegarde en cours...',
        color: 'text-blue-600',
        bg: 'bg-blue-50'
      };
    }
    
    if (isDirty) {
      return {
        icon: <Clock className="h-3 w-3" />,
        text: 'Sauvegarde dans 5s...',
        color: 'text-orange-600',
        bg: 'bg-orange-50'
      };
    }
    
    return {
      icon: <CheckCircle className="h-3 w-3" />,
      text: lastSaved ? `Sauvegardé ${timeAgo}` : 'Sauvegardé',
      color: 'text-green-600',
      bg: 'bg-green-50'
    };
  };

  const status = getStatus();

  return (
    <div className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-xs font-medium font-['Bricolage_Grotesque'] ${status.color} ${status.bg} border border-current/20 transition-all duration-200`}>
      {status.icon}
      <span>{status.text}</span>
      {isOnline && <Wifi className="h-3 w-3 opacity-50" />}
    </div>
  );
};